import QuestionCircleOutlined from '@ant-design/icons/QuestionCircleOutlined'
import { Checkbox, Select, Tooltip } from 'antd'
import { useOrgSite } from 'features/fde'
import { useInbox } from 'features/inbox/contexts/InboxContext'
import { useInspectionData } from 'features/inbox/hooks/useInspectionData'
import { InboxSortDirection } from 'features/inbox/types/types'
import React, { memo, useCallback, useMemo } from 'react'
import styled from 'styled-components'
import CantileverTheme from 'styles/cantileverTheme'

const HeaderContainer = styled.div`
  background: rgba(55, 55, 55, 1);
  padding: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
`

const SortSection = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const FutureInspectionsSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.25rem;
`

const FutureRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const Subtext = styled.div`
  color: ${CantileverTheme.colors.grayscale['500']};
  font-size: 0.75rem;
`

const TooltipIcon = styled(QuestionCircleOutlined)`
  color: ${CantileverTheme.colors.grayscale['400']};
  font-size: 0.875rem;
  cursor: help;
`

const SortSelect = styled(Select)`
  min-width: 8rem;
`

const ControlsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  width: 100%;
`

const InboxListHeaderComponent: React.FC = () => {
  const { state, setFilters, setSortDirection } = useInbox()
  const { siteId, orgId } = useOrgSite()

  const handleSortChange = useCallback(
    (value: InboxSortDirection) => {
      setSortDirection(value)
    },
    [setSortDirection]
  )

  const handleShowFutureInspectionsChange = useCallback(
    (checked: boolean) => {
      setFilters({ showFutureInspections: checked })
    },
    [setFilters]
  )

  // Compute hidden count relative to the future-inspection filter only
  const filtersWithFuture = useMemo(
    () => ({ ...state.filters, showFutureInspections: true }),
    [state.filters]
  )
  const filtersWithoutFuture = useMemo(
    () => ({ ...state.filters, showFutureInspections: false }),
    [state.filters]
  )

  const { inspections: withFutureInspections } = useInspectionData({
    siteId: siteId ?? undefined,
    orgId: orgId ?? undefined,
    sortDirection: state.sortDirection,
    filters: filtersWithFuture,
  })
  const { inspections: withoutFutureInspections } = useInspectionData({
    siteId: siteId ?? undefined,
    orgId: orgId ?? undefined,
    sortDirection: state.sortDirection,
    filters: filtersWithoutFuture,
  })

  const shownCount = useMemo(
    () =>
      state.filters.showFutureInspections
        ? withFutureInspections.length
        : withoutFutureInspections.length,
    [
      state.filters.showFutureInspections,
      withFutureInspections.length,
      withoutFutureInspections.length,
    ]
  )
  const hiddenCount = useMemo(
    () => withFutureInspections.length - shownCount,
    [withFutureInspections.length, shownCount]
  )

  return (
    <HeaderContainer>
      <ControlsRow>
        <FutureInspectionsSection>
          <FutureRow>
            <Checkbox
              checked={state.filters.showFutureInspections}
              onChange={(e) =>
                handleShowFutureInspectionsChange(e.target.checked)
              }>
              Show future inspections
            </Checkbox>
            <Tooltip
              title='By default, inspections starting more than 2 weeks from today are not shown.'
              placement='top'
              color={CantileverTheme.colors.grayscale['500']}>
              <TooltipIcon />
            </Tooltip>
            <Subtext>{hiddenCount > 0 ? `${hiddenCount} hidden` : ''}</Subtext>
          </FutureRow>
        </FutureInspectionsSection>

        <SortSection>
          <span
            style={{
              color: CantileverTheme.colors.grayscale['400'],
              fontSize: '0.875rem',
            }}>
            Sort:
          </span>
          <SortSelect
            value={state.sortDirection}
            onChange={(v) => handleSortChange(v as InboxSortDirection)}
            options={[
              { value: 'newest', label: 'Newest first' },
              { value: 'oldest', label: 'Oldest first' },
            ]}
          />
        </SortSection>
      </ControlsRow>
    </HeaderContainer>
  )
}

export const InboxListHeader = memo(InboxListHeaderComponent)
